/**
 * 분할 보기 — 좌 실시간 / 우 긴호흡 기록의 상태·판정을 담는 순수 모듈.
 *
 * `feed-order.ts` / `use-feed-order.ts` 의 분리를 그대로 따른다: 순수
 * 함수는 라우트 없이 단위 테스트가 된다(docs/SPLITVIEW_DESIGN.md D1-1).
 */

import type { FeedItem } from './reducer'

/**
 * 보기 모드 3값(SPLITVIEW_ROWS_DESIGN D3-1).
 *  · `unified` — 통합 타임라인(오늘과 동일).
 *  · `split`   — v4 2열 독립 스크롤. **그대로 남긴다**: 문단이 안 오는 방
 *    (`TRANS_LONGFORM` OFF)에서는 행 정렬이 대기 행 하나로 끝나므로 이쪽이
 *    우월하다.
 *  · `rows`    — v5 긴호흡 문단 행 정렬(기본).
 */
export type SplitMode = 'unified' | 'split' | 'rows'
export type SplitPanel = 'live' | 'longform'

/** 영속 값은 문자열 리터럴이다 — boolean 은 저장하지 않는다(D1-3). */
export const DEFAULT_SPLIT_MODE: SplitMode = 'unified'

/**
 * 역할별 기본 보기 — **호스트·뷰어 모두 통합('unified')**이다
 * (사용자 결정 2026-09-22 07:0xZ "1. 통합" — TASK_split_default_unified.md).
 * `split`·`rows` 는 선택지로 계속 남는다: 저장값은 그대로 읽히고
 * (`isSplitMode`), UI 에서도 계속 고를 수 있다.
 *
 * 역할을 인자로 남겨 두는 이유: 한쪽만 바꿔야 할 때 이 함수 한 곳만 고치면
 * 되고, 호출부(`use-split-view`)가 이미 역할을 넘긴다. 한 상수로 묶으면
 * 한쪽을 바꿀 때 다른 쪽이 조용히 따라 바뀐다.
 *
 * **저장된 선택이 항상 이긴다** — 이 값은 저장값이 없을 때만 쓰인다.
 * `DEFAULT_SPLIT_MODE`(='unified')와 일치시킨다.
 */
export function defaultSplitMode(_role: 'host' | 'viewer'): SplitMode {
  return 'unified'
}

/**
 * "분할 보기 켜기" 의 목표값 — `defaultSplitMode` 가 'unified' 가 되면서
 * (2026-09-22) 켜기·끄기가 더는 대칭이 아니다: 끄기는 `'unified'` 로
 * 가지만, 켜기는 **`'unified'` 가 아닌 값**으로 가야 스위치·토글이 의미가
 * 있다. `'split'`(v4 2열)을 그 목표로 고정한다 — `'rows'` 는 저장된
 * 선택으로만 남고 켜기 동작의 목적지는 아니다.
 */
export const NON_UNIFIED_DEFAULT_SPLIT_MODE: SplitMode = 'split'

/** 호스트·뷰어가 각자 기억한다 — feed-order.ts:32-34 와 같은 규약(D1-2). */
export function splitStorageKey(role: 'host' | 'viewer'): string {
  return `omnitrans:split-view:${role}`
}

/**
 * 저장된 `'rows'`·`'split'` 은 사용자 결정(2026-09-22)에 따라
 * `use-split-view.ts` 가 1회 기본값('unified')으로 교체한다(마이그레이션
 * 표식 `:migrated-default-v1`, 이미 표식이 있으면 존중) — 이 모듈
 * (`isSplitMode`)은 여전히 세 값 모두를 유효한 저장값으로 인정한다.
 */
export function isSplitMode(value: unknown): value is SplitMode {
  return value === 'unified' || value === 'split' || value === 'rows'
}

/** CaptionFeed 의 kinds 프롭 값 — 패널당 한 벌(D2-3). */
export const LIVE_KINDS: readonly FeedItem['kind'][] = ['partial', 'final', 'gap']
export const LONGFORM_KINDS: readonly FeedItem['kind'][] = ['paragraph']

/**
 * 항목이 속한 패널 — D2-3 표의 단일 판정처.
 * `consolidatedInto` 유무와 무관하게 final 은 항상 live 다(D4).
 */
export function panelOf(item: FeedItem): SplitPanel {
  return item.kind === 'paragraph' ? 'longform' : 'live'
}

/** D7-2 보존 대상. */
export interface PanelScrollState {
  anchorId: string | null
  anchorOffset: number
  atNewest: boolean
  unseen: number
  seenSeq: number
}
